import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  updateSection,
  setSection,
} from "../../../../../../features/assignment/assignmentSlice";

import { tagInfo, phraseInfo } from "../constants";

import { getContentHeight } from "../../../../../../utils/textUtils";
import { getStartingIndex } from "../../../../../../utils/resumeUtils";
import { compareGroupData, comparePhraseData } from "./handleSubmit";
import { 
  evaluateAdvancedSyntax
} from "../../../../../../utils/syntaxEvaluation";
import { 
  updateAdvancedPageWithCorrectness
} from "../../../../../../utils/syntaxDataHelpers";

import AdvancedGroupElement from "./AdvancedGroupElement";
import ArrowButton from "../../../widgets/ArrowButton";
import CorrectOverlay from "../../../widgets/CorrectOverlay";
import Toast from "../../../widgets/Toast";
import TagButtonList from "../TagButtonList";
import { refinePageList } from "./refinePageList";
import AdvancedTokenElement from "./AdvancedTokenElement";

const AdvancedPage = ({ section, setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const [pageList, setPageList] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(-1);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayType, setOverlayType] = useState('correct'); // 'correct' or 'incorrect'
  const [elementCorrectness, setElementCorrectness] = useState({});
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const lineHeight = "50px";
  const currentPage = pageList[currentIndex] || {
    type: "",
    data: [],
    tags: [],
  };

  useEffect(() => {
    if (pageList.length > 0) {
      return;
    }
    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }
    if (section.question) {
      refinePageList({ setPageList, section });
    }
  }, [section]);

  const handleClickToken = (element, elementIndex) => {
    if (currentPage.isSubmitted) {
      return;
    }
    const pageData = currentPage.data.slice();
    const newGroup = {
      type: "group",
      text: [
        {
          type: element.type,
          text: element.text,
          index: element.index,
          space_right: element.space_right,
        },
      ],
    };
    pageData.splice(elementIndex, 1, newGroup);

    let newPageList = [];

    if (selectedGroupIndex === -1) {
      setSelectedGroupIndex(elementIndex);
      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            data: pageData,
          };
        }
        return page;
      });
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));
    } else {
      const selectedGroup = pageList[currentIndex].data[selectedGroupIndex];
      if (
        elementIndex > selectedGroupIndex &&
        selectedGroup.text.length === 1 &&
        currentPage.data
          .slice(selectedGroupIndex + 1, elementIndex + 1)
          .every((block) => block.type === "token")
      ) {
        newPageList = pageList.map((page, pageIndex) => {
          if (pageIndex === currentIndex) {
            const pageData = page.data.slice();
            const newGroup = {
              ...selectedGroup,
              text: [
                ...selectedGroup.text,
                ...pageData.slice(selectedGroupIndex + 1, elementIndex + 1),
              ],
            };
            pageData.splice(
              selectedGroupIndex,
              elementIndex - selectedGroupIndex + 1,
              newGroup
            );
            return {
              ...page,
              data: pageData,
            };
          }
          return page;
        });
        setPageList(newPageList);
        dispatch(setSection({ ...section, answer: newPageList }));
      }
    }
  };

  const handleClickGroup = (element, elementIndex) => {
    if (currentPage.isSubmitted) {
      return;
    }

    const currentPageData = currentPage.data.slice();
    currentPageData.splice(elementIndex, 1, ...element.text);
    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          data: currentPageData,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    setSelectedGroupIndex(-1);
  };

  const handleClickTag = (tag) => {
    insertTagData({
      tag: tag,
    });
    setSelectedGroupIndex(-1);
  };

  const handlePrev = () => {
    if (selectedGroupIndex !== -1) {
      handleClickGroup(
        pageList[currentIndex].data[selectedGroupIndex],
        selectedGroupIndex
      );
    }
    setCurrentIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < pageList.length - 1) {
      if (selectedGroupIndex !== -1) {
        handleClickGroup(
          pageList[currentIndex].data[selectedGroupIndex],
          selectedGroupIndex
        );
      }
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllSubmitted = true;
      pageList.forEach((page) => {
        if (page.isSubmitted === false) {
          isAllSubmitted = false;
          return;
        }
      });
      if (isAllSubmitted) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습을 완료하셨습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const insertTagData = ({ tag }) => {
    if (selectedGroupIndex === -1) {
      return;
    }

    const allTagList = Object.keys(tagInfo);

    const currentPage = pageList[currentIndex];
    const selectedGroup = currentPage.data[selectedGroupIndex];

    let newPageList = [];

    if (allTagList.includes(tag)) {
      const groupData = Object.assign({}, currentPage.groupData);
      const groupLabelIndex = Math.floor(selectedGroup.text.length / 2);

      for (let i = 0; i < selectedGroup.text.length; i++) {
        groupData[selectedGroup.text[i].index] = {
          groupType: tag,
          isGroupEnd: i === selectedGroup.text.length - 1,
          isGroupLabel: i === groupLabelIndex,
          space_right: selectedGroup.text[i].space_right,
        };
      }

      const pageData = currentPage.data.slice();
      pageData.splice(selectedGroupIndex, 1, ...selectedGroup.text);

      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            data: pageData,
            groupData: groupData,
          };
        }
        return page;
      });
    } else {
      const startIndex = selectedGroup.text[0].index;
      const endIndex = selectedGroup.text[selectedGroup.text.length - 1].index;
      const phraseData = { ...currentPage.phraseData };

      let startData = {};
      let endData = {};

      if (phraseData[startIndex]) {
        startData = [...phraseData[startIndex].start];
        startData.push({
          ...phraseInfo[tag],
          endIndex: endIndex,
        });
        startData.sort((a, b) => b.endIndex - a.endIndex);
        endData = phraseData[startIndex].end;
      } else {
        startData = [{ ...phraseInfo[tag], endIndex: endIndex }];
        endData = [];
      }
      phraseData[startIndex] = {
        start: startData,
        end: endData,
      };

      if (phraseData[endIndex]) {
        endData = [...phraseData[endIndex].end];
        endData.push({
          ...phraseInfo[tag],
          startIndex: startIndex,
        });
        endData.sort((a, b) => b.startIndex - a.startIndex);
        startData = phraseData[endIndex].start;
      } else {
        startData = [];
        endData = [{ ...phraseInfo[tag], startIndex: startIndex }];
      }
      phraseData[endIndex] = {
        start: startData,
        end: endData,
      };

      const pageData = currentPage.data.slice();
      pageData.splice(selectedGroupIndex, 1, ...selectedGroup.text);

      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            data: pageData,
            phraseData: phraseData,
          };
        }
        return page;
      });
    }
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) {
      return;
    }

    const submitCount = currentPage.submitCount + 1;

    let evaluationResult;
    let newCurrentPage;

    // Check if max attempts (3) reached
    if (submitCount >= 3) {
      // Auto-reveal all correct answers after 3 attempts
      const allTagList = Object.keys(tagInfo);
      const allPhraseList = Object.keys(phraseInfo);

      const autoRevealedGroupData = {};
      const autoRevealedPhraseData = {};

      // Process answerData to reveal correct answers
      currentPage.answerData.forEach(answer => {
        if (allTagList.includes(answer.type)) {
          // Add to groupData
          const groupLabelIndex = Math.floor(answer.token_indices.length / 2);
          answer.token_indices.forEach((tokenIndex, idx) => {
            autoRevealedGroupData[tokenIndex] = {
              groupType: answer.type,
              isGroupEnd: idx === answer.token_indices.length - 1,
              isGroupLabel: idx === groupLabelIndex,
              space_right: currentPage.data[tokenIndex]?.space_right || true
            };
          });
        } else if (allPhraseList.includes(answer.type)) {
          // Add to phraseData
          const startIndex = answer.token_indices[0];
          const endIndex = answer.token_indices[answer.token_indices.length - 1];

          if (!autoRevealedPhraseData[startIndex]) {
            autoRevealedPhraseData[startIndex] = { start: [], end: [] };
          }
          autoRevealedPhraseData[startIndex].start.push({
            ...phraseInfo[answer.type],
            endIndex: endIndex
          });

          if (!autoRevealedPhraseData[endIndex]) {
            autoRevealedPhraseData[endIndex] = { start: [], end: [] };
          }
          autoRevealedPhraseData[endIndex].end.push({
            ...phraseInfo[answer.type],
            startIndex: startIndex
          });
        }
      });

      // Mark all answers as submitted
      const autoRevealedAnswers = currentPage.answerData.map(answer => ({
        ...answer,
        isSubmitted: true,
        isCorrect: true,
        studentAnswer: answer.type,
        autoRevealed: true
      }));

      newCurrentPage = {
        ...currentPage,
        groupData: autoRevealedGroupData,
        phraseData: autoRevealedPhraseData,
        answerData: autoRevealedAnswers,
        submitCount: submitCount,
        isSubmitted: true,
        autoRevealed: true
      };

      setOverlayType('incorrect'); // Show X overlay for max attempts
    } else {
      // Normal evaluation (attempts 1-2)
      evaluationResult = evaluateAdvancedSyntax(
        currentPage.groupData,
        currentPage.phraseData,
        currentPage.answerData,
        currentIndex
      );

      // Update page with correctness information
      newCurrentPage = updateAdvancedPageWithCorrectness(currentPage, evaluationResult);
      newCurrentPage.submitCount = submitCount;
      newCurrentPage.isSubmitted = evaluationResult.isAllCorrect;

      // Store correctness for overlay display
      setElementCorrectness(evaluationResult.correctness);
      setOverlayType(evaluationResult.isAllCorrect ? 'correct' : 'incorrect');
    }

    // Update page list
    const newPageList = pageList.map((prevPage, index) => {
      if (index === currentIndex) {
        return newCurrentPage;
      }
      return prevPage;
    });

    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    dispatch(
      updateSection({ sectionId: section.id, data: { answer: newPageList } })
    );

    // Show overlay feedback or toast
    if (submitCount < 3) {
      setOverlayVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);

        // Auto-advance if all correct
        if (newCurrentPage.isSubmitted) {
          setTimeout(() => {
            if (currentIndex < pageList.length - 1) {
              setCurrentIndex(currentIndex + 1);
            }
          }, 200);
        }
      }, 600);
    } else {
      // Max attempts reached - show X overlay and toast (user must manually navigate)
      setOverlayVisible(true);
      setToastMessage("제출 횟수를 초과하였습니다.\n정답을 확인하고, 다음 문제로 넘어가세요.");
      setToastVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);
      }, 600);
      setTimeout(() => {
        setToastVisible(false);
      }, 3000);
    }
  };

  const deleteData = () => {
    setPageList(
      pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            groupData: {},
            phraseData: {},
          };
        }
        return page;
      })
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingRight: "20px",
          justifyContent: "space-between",
          height: getContentHeight(),
          fontFamily: '"Noto Sans KR", sans-serif',
          backgroundColor: "#FFFFFF",
        }}
      >
        <ArrowButton
          direction="left"
          onClick={() => handlePrev()}
          visibleCondition={currentIndex > 0}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "90%",
            height: "100%",
            gap: "20px",
          }}
        >
          <div
            style={{
              fontSize: "20px",
              marginBottom: "20px",
            }}
          >
            아래 표시된 단어 (구, 절)의 문장 형식을 찾아 선택하시오.
          </div>
          <div
            style={{
              fontSize: "28px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "50px",
            }}
          >
            {currentPage.type === "syntax" && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  whiteSpace: "pre-wrap",
                }}
              >
                {currentPage.data.map((element, elementIndex) => {
                  return (
                    <>
                      {element.type === "token" && (
                        <AdvancedTokenElement
                          currentPage={currentPage}
                          handleClickToken={handleClickToken}
                          element={element}
                          elementIndex={elementIndex}
                          lineHeight={lineHeight}
                        />
                      )}
                      {element.type === "group" && (
                        <AdvancedGroupElement
                          currentPage={currentPage}
                          handleClickGroup={handleClickGroup}
                          element={element}
                          elementIndex={elementIndex}
                          lineHeight={lineHeight}
                          isSelectable={!currentPage.isSubmitted}
                        />
                      )}
                    </>
                  );
                })}
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: "30px",
            }}
          >
            <TagButtonList
              tagList={currentPage.tags}
              onClick={handleClickTag}
            />
          </div>
        </div>
        <ArrowButton
          direction="right"
          onClick={() => handleNext()}
          visibleCondition={true}
        />
        <button
          style={{
            position: "absolute", // 부모(80% width) 안에서 절대 위치
            top: "90px", // 최하단으로부터 10px 떨어짐
            right: "60px", // 우측으로부터 10px 떨어짐
            padding: "10px 35px",
            border: "none",
            fontSize: "18px",
            borderRadius: "5px",
            cursor: "pointer",
            backgroundColor: "#FF4444",
            color: "white",
          }}
          onClick={() => deleteData()}
        >
          초기화
        </button>
        <button
          style={{
            position: "absolute", // 부모(80% width) 안에서 절대 위치
            bottom: "90px", // 최하단으로부터 10px 떨어짐
            right: "60px", // 우측으로부터 10px 떨어짐
            padding: "10px 35px",
            backgroundColor: currentPage.isSubmitted ? "#EBEBEB" : "#2D2D2D",
            color: currentPage.isSubmitted === true ? "#737373" : "white",
            border: "none",
            fontSize: "18px",
            borderRadius: "5px",
            cursor: "pointer",
          }}
          onClick={() => handleSubmit()}
        >
          제출하기
        </button>
      </div>
      <div
        style={{
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay bool={overlayType === 'correct'} />
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          height: "70px",
          width: "100vw",
          backgroundColor: "#F6F8FA", // 바 색상
          color: "#737373",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {currentIndex + 1} / {pageList.length}
      </div>
      <Toast message={toastMessage} visible={toastVisible} />
    </>
  );
};

export default AdvancedPage;
